import React from "react";
import { FiDownload } from "react-icons/fi";
import Button from "components/ui/Button";
import {
  useGetSystemTemplatesQuery,
  useDownloadSystemTemplateSample,
} from "apis/templateLibrary";

// The marketing templates Beleef maintains in code, for one network.
//
// Read-only on purpose: these are modules in the backend, not files uploaded
// here. What an admin can do is check what a brand can print and pull a sample
// to measure against a printer's spec.
//
// "Complete" is computed by the backend at request time by attempting the
// render, so a template that cannot produce a sample never offers a download.

const TYPE_LABELS = {
  signboard: "Signboards",
  brochure: "Brochures",
  mailcard: "Mailcards",
  letterhead: "Letterhead",
  community: "Community",
};

// Same shape as ASSET_STATUS in utils/network, so a template's state reads the
// way a brand asset's does one screen over.
const RENDER_STATUS = {
  complete: { label: "Renders", className: "bg-green-100 text-green-800" },
  broken: { label: "Not renderable", className: "bg-red-100 text-red-800" },
};

// Printers quote two numbers and reject artwork for either. Trim is the visible
// face the design occupies; final is the PDF they actually want, trim plus
// bleed on every edge. Both are shown because checking only one is how a board
// comes back rejected.
const formatBox = (box) =>
  box?.width && box?.height ? `${box.width} × ${box.height}` : null;

const StatusPill = ({ status }) => (
  <span
    className={`flex-shrink-0 rounded-full px-2 py-0.5 text-[10px] font-medium ${status.className}`}
  >
    {status.label}
  </span>
);

const TemplateRow = ({ template, networkId, onDownload, downloadingId }) => {
  const trim = formatBox(template.trim);
  const final = formatBox(template.final);
  const isDownloading = downloadingId === template.id;

  return (
    <div className="flex flex-col gap-2.5 border-t border-mediumgray px-3.5 py-3 first:border-t-0 sm:flex-row sm:items-start sm:gap-3">
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-[13px] font-medium text-black">
            {template.name}
          </span>
          <span className="rounded bg-lightgray px-1.5 py-0.5 text-[10px] text-darkgray">
            {template.size}
          </span>
          <StatusPill
            status={
              template.complete ? RENDER_STATUS.complete : RENDER_STATUS.broken
            }
          />
        </div>

        {trim ? (
          <div className="mt-1 flex flex-wrap gap-x-3 gap-y-0.5 text-[11px] text-darkgray">
            <span>
              <span className="text-black">Trim</span> {trim}
            </span>
            <span>
              <span className="text-black">Final</span> {final}
            </span>
            {template.bleedMm ? <span>{template.bleedMm}mm bleed</span> : null}
          </div>
        ) : null}

        {template.description ? (
          <div className="mt-1 truncate text-[11px] text-darkgray">
            {template.description}
          </div>
        ) : null}

        {!template.complete && template.reason ? (
          <div className="mt-1 text-[11px] text-red-600">{template.reason}</div>
        ) : null}

        <div className="mt-1 truncate text-[10px] text-darkgray">
          {template.id}
        </div>
      </div>

      {template.complete ? (
        <div className="sm:flex-shrink-0">
          <Button
            className="outline-button flex items-center gap-1.5 !py-1.5 !px-2.5 !text-[12px]"
            loading={isDownloading}
            onClick={() => onDownload({ networkId, template })}
          >
            <FiDownload className="h-3.5 w-3.5" />
            Download sample
          </Button>
        </div>
      ) : null}
    </div>
  );
};

const SystemTemplatesBrowser = ({ networkId }) => {
  const { data, isPending, isError, error } =
    useGetSystemTemplatesQuery(networkId);
  const {
    mutate: download,
    variables,
    isPending: isDownloading,
  } = useDownloadSystemTemplateSample();

  // Not PageLoader — that is h-screen, and this renders inside a page that has
  // already drawn its heading and breadcrumb.
  if (isPending) {
    return (
      <p className="py-6 text-[13px] text-darkgray">
        <i className="fa-solid fa-spinner animate-spin mr-2"></i>
        Rendering templates …
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-[13px] text-red-600">
        Error loading system templates: {error?.message}
      </p>
    );
  }

  const groups = Object.entries(data?.templates || {});

  if (!groups.length) {
    return (
      <p className="text-[13px] italic text-darkgray">
        No coded templates for this network yet
      </p>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-[12px] text-darkgray">
        Built and maintained in the Beleef codebase — not uploaded here. Samples
        use stock property data with this network&apos;s own logo and agents,
        and render at true print size.
      </p>

      {!data?.canRender ? (
        <p className="text-[12px] text-red-600">
          No active group under this network, so nothing can be rendered against
          its branding.
        </p>
      ) : null}

      {groups.map(([type, templates]) => (
        <div key={type}>
          <h2 className="mb-1.5 text-[11px] font-medium uppercase tracking-wider text-darkgray">
            {TYPE_LABELS[type] || type} · {templates.length}
          </h2>
          <div className="rounded-lg border border-mediumgray bg-white">
            {templates.map((template) => (
              <TemplateRow
                key={template.id}
                template={template}
                networkId={networkId}
                onDownload={download}
                downloadingId={isDownloading ? variables?.template?.id : null}
              />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

export default SystemTemplatesBrowser;
